import React, { useState } from "react";
import { ChevronDown, HelpCircle, Search, Key, Truck, Shield } from "lucide-react";

const FAQ_CATEGORIES = [
  { id: "all", label: "All Questions" },
  { id: "tenants", label: "For Renters (FIND)" },
  { id: "landlords", label: "For Landlords (LET)" },
  { id: "move", label: "FIND LET Move" },
  { id: "security", label: "Verification & Security" }
];

const FAQS = [
  {
    category: "tenants",
    q: "How does the FIND brief system work for renters?",
    a: "Instead of searching across dozens of portals, you define your search criteria once (budget, boroughs, move date, pets, space). Our concierge desk matches your brief directly with private landlords and partner inventory, pushing curated matches to your portal."
  },
  {
    category: "tenants",
    q: "Is there any cost for renters to submit a brief?",
    a: "Submitting a search brief and receiving matches from the FIND LET Concierge Desk is completely free for tenants. We adhere to the Tenant Fees Act 2019."
  },
  {
    category: "tenants",
    q: "How fast will I receive matched property options?",
    a: "Most renters receive their first hand-curated matches within 4 to 12 hours of brief submission. For highly urgent move-in dates, our concierge desk prioritizes instant manual outreach."
  },
  {
    category: "landlords",
    q: "How does FIND LET differ from traditional letting portals?",
    a: "Traditional portals publish public listings that attract hundreds of unqualified enquiries. FIND LET only introduces pre-screened tenants whose confirmed budgets, move windows, and household requirements match your property specifications."
  },
  {
    category: "landlords",
    q: "Can I request property preparation services before tenant move-in?",
    a: "Yes. In your landlord portal or listing form, you can tick optional FIND LET Prep services including professional deep cleaning, Gas Safety & EPC certificates, and certified inventory check-ins."
  },
  {
    category: "move",
    q: "What services are included in FIND LET Move?",
    a: "FIND LET Move provides turnkey relocation support including white-glove removals, delicate artwork crating, end-of-tenancy deep cleaning with deposit sign-off guarantee, climate-controlled secure storage, and utilities setup."
  },
  {
    category: "security",
    q: "How are properties and landlords verified?",
    a: "Every listing in the active pool undergoes ownership and title verification, EPC/safety compliance checks, and photo authentication by our operations team before being matched to tenants."
  }
];

export const Faqs = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [openFaq, setOpenFaq] = useState(0);

  const filteredFaqs = activeTab === "all" ? FAQS : FAQS.filter(f => f.category === activeTab);

  return (
    <div className="min-h-screen bg-[#F9FBFF] py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-10">
        
        {/* Title */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#D0E4FE] text-blue-800 text-xs font-semibold">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Frequently Asked Questions</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#091F5C] font-heading">
            Clear Answers for London Renters & Owners
          </h1>
          <p className="text-[#4A5B78] text-sm max-w-lg mx-auto">
            Everything you need to know about our matching protocols, concierge desks, and move dispatch.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2">
          {FAQ_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              data-testid={`faq-tab-${cat.id}`}
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                activeTab === cat.id 
                  ? "bg-[#091F5C] text-white shadow-md" 
                  : "bg-white text-[#4A5B78] border border-[#E5EBF5] hover:border-[#D0E4FE]"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Accordion List */}
        <div className="space-y-3">
          {filteredFaqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-[#E5EBF5] overflow-hidden shadow-sm transition-all"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  data-testid={`faq-toggle-${idx}`}
                  className="w-full p-5 text-left flex items-center justify-between space-x-4 focus:outline-none"
                >
                  <span className="font-bold text-[#091F5C] text-sm font-heading">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-[#4A5B78] transition-transform ${isOpen ? "rotate-180 text-[#334DAF]" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-xs text-[#4A5B78] leading-relaxed border-t border-slate-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};
